import { useRouter } from 'next/router'
import React, { useState, useMemo } from 'react'
import { Popover, Row, Col, message } from 'antd'
import classNames from 'classnames'
import styles from './WhiteUserFrontControl.module.css'

import { MyModal } from '~/components/ui/MyModal'
import { MyBtn } from '~/components/ui/MyBtn'
import { useAuth } from '~/hooks/useAuth'
import { observer } from 'mobx-react'

import {
  OPERATE_TYPE,
  useWhiteUserFrontControl,
  useWhiteUserOptions,
} from './useWhiteUserControl'

import { PostTypes, VisibleStatus } from '~/graphqls/types'
import {
  Eye,
  IconDelete,
  IconFeatured,
  IconSetting3,
  IconUser,
} from 'wujieai-react-icon'

/** 白名单用户前台操作 */
const WhiteUserFrontControlMain: React.FC<{
  className?: string
  post: PostTypes
  /** 是否展示推荐功能 */
  showRecommend?: boolean
  /** 是否来自广场 */
  isSquare?: boolean
}> = ({ className, post, showRecommend = true, isSquare }) => {
  const [visible, setVisible] = useState(false)
  // 删除弹窗
  const [delVisible, setDelVisible] = useState(false)
  // 选中删除理由index
  const [selectDelIndex, setSelectDelIndex] = useState<number | null>(null)

  const { getDelReasonList, delReasonList } = useWhiteUserOptions()
  const { isPlazaWhiteListUser } = useAuth()
  const router = useRouter()

  /** 隐藏删除原因弹窗 */
  const hideDelVisible = () => {
    setDelVisible(false)
    setSelectDelIndex(null)
  }

  const {
    isRecommended,
    handleRecommendClick,
    isMuted,
    handleMuteClick,
    handleDelPostClick,
    delLoading,
    handleHidePostClick,
  } = useWhiteUserFrontControl({
    post,
    onSuccess: ({ key }) => {
      if (key === OPERATE_TYPE.DELETE || key === OPERATE_TYPE.HIDDEN) {
        if (key === OPERATE_TYPE.DELETE) {
          hideDelVisible()
        }
        if (isSquare) {
          router.reload()
        } else {
          router.back()
        }
      }
    },
  })

  /** 可操作选项 */
  const Items = useMemo<
    {
      /** 为激活时标签 */
      label: React.ReactNode
      /** 已激活时标签样式 */
      activeLabel: React.ReactNode
      /** 是否激活状态 */
      isActive: Boolean
      /** 是否显示 */
      show: Boolean
      /** 点击时操作 */
      onClick: () => void
    }[]
  >(() => {
    return [
      {
        label: (
          <>
            <IconFeatured size={16} className="mr-1" />
            推荐作品
          </>
        ),
        activeLabel: (
          <>
            <IconFeatured size={16} className="mr-1 text-[#FFD600]" />
            移除推荐
          </>
        ),
        isActive: isRecommended,
        show: showRecommend,
        onClick: handleRecommendClick,
      },
      {
        label: (
          <>
            <Eye size={16} className="mr-1" />
            隐藏帖子
          </>
        ),
        show: true,
        activeLabel: null,
        isActive: false,
        onClick: handleHidePostClick,
      },
      {
        label: (
          <>
            <IconDelete size={16} className="mr-1" />
            删除帖子
          </>
        ),
        show: true,
        activeLabel: null,
        isActive: false,
        onClick: async () => {
          // 获取配置
          await getDelReasonList()
          setDelVisible(true)
        },
      },
      {
        label: (
          <>
            <IconUser size={16} className="mr-1" />
            禁言用户
          </>
        ),
        activeLabel: (
          <>
            <IconUser size={16} className="mr-1 text-[#EF466F]" />
            已禁言
          </>
        ),
        show: true,
        isActive: isMuted,
        onClick: handleMuteClick,
      },
    ]
  }, [
    showRecommend,
    isRecommended,
    isMuted,
    handleRecommendClick,
    handleMuteClick,
  ])
  // 非白名单用户 或者帖子不可见 隐藏
  if (!isPlazaWhiteListUser || post.visible === VisibleStatus.NOT_VISIBLE) {
    return null
  }
  return (
    <>
      <Popover
        overlayClassName={styles.overlayClass}
        open={visible}
        onOpenChange={open => {
          setVisible(open)
        }}
        content={
          <div className="text-sm text-neutral-500 font-semibold ">
            {Items.filter(it => it.show).map((item, index) => (
              <div key={index}>
                {index !== 0 && <div className="h-[1px] bg-neutral-300 mx-4" />}
                <div
                  className="flex items-center justify-center  px-[25px] py-3 cursor-pointer hover:bg-neutral-300"
                  onClick={() => {
                    setVisible(false)
                    item?.onClick()
                  }}
                >
                  {item?.isActive ? item?.activeLabel : item?.label}
                </div>
              </div>
            ))}
          </div>
        }
        trigger="hover"
      >
        <div
          className={classNames('flex items-center cursor-pointer ', className)}
        >
          <IconSetting3 size={16} />
        </div>
      </Popover>
      {!!delReasonList?.length && (
        <MyModal
          dark
          open={delVisible}
          title="请选择删除原因"
          onCancel={hideDelVisible}
          maskStyle={{
            backgroundColor: 'rgba(0,0,0,0.8)',
          }}
          width={448}
          footer={null}
          transitionName=""
          maskTransitionName=""
          centered
        >
          <Row gutter={4}>
            {delReasonList.map((item, index) => (
              <Col key={index} span={8}>
                <div
                  className={classNames(styles.tag, {
                    [styles.tagActive]: selectDelIndex === index,
                  })}
                  onClick={() => {
                    setSelectDelIndex(index)
                  }}
                >
                  {item}
                </div>
              </Col>
            ))}
          </Row>
          <div className="flex justify-between mt-3">
            <MyBtn
              type="default"
              $dark
              block
              size="large"
              className="!bg-neutral-200 !border-neutral-200"
              onClick={hideDelVisible}
            >
              取消
            </MyBtn>
            <MyBtn
              $dark
              type="primary"
              block
              size="large"
              className="ml-4"
              loading={delLoading}
              onClick={() => {
                message.destroy()
                if (!selectDelIndex && selectDelIndex !== 0) {
                  message.error('请选择删除原因')
                  return
                }
                handleDelPostClick({
                  delReason: delReasonList[selectDelIndex]!,
                })
              }}
            >
              确认删除
            </MyBtn>
          </div>
        </MyModal>
      )}
    </>
  )
}

export const WhiteUserFrontControl = observer(WhiteUserFrontControlMain)
